"use client";

import { KeyboardEvent, PointerEvent, useRef, useState } from "react";
import { HandSwipeLeftIcon, SealCheckIcon } from "@phosphor-icons/react/ssr";
import Image from "next/image";

import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { Result } from "./data";

type ResultCardProps = {
    result: Result;
};

const SLIDER_STEP = 5;

export default function ResultCard({ result }: ResultCardProps) {
    const { name, age, quote, alt, beforeImageUrl, afterImageUrl } = result;
    const sendEvent = useTrackEvent();
    const containerRef = useRef<HTMLElement>(null);
    const [position, setPosition] = useState(50);

    const trackImageInteraction = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "improvement_results",
                meta_data: {
                    image_person_name: name,
                    product_name: "anti-aging face cream",
                },
            },
        });
    };

    const handleMove = (clientX: number) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percent = (x / rect.width) * 100;

        setPosition(Math.max(0, Math.min(100, percent)));
    };

    const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
        event.preventDefault();
        trackImageInteraction();

        const target = event.currentTarget;
        target.setPointerCapture(event.pointerId);

        const move = (pointerEvent: globalThis.PointerEvent) => {
            handleMove(pointerEvent.clientX);
        };

        const up = (pointerEvent: globalThis.PointerEvent) => {
            target.releasePointerCapture(pointerEvent.pointerId);
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
        };

        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!["ArrowLeft", "ArrowDown", "ArrowRight", "ArrowUp", "Home", "End"].includes(event.key)) {
            return;
        }

        trackImageInteraction();

        if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
            event.preventDefault();
            setPosition((current) => Math.max(0, current - SLIDER_STEP));
            return;
        }

        if (event.key === "ArrowRight" || event.key === "ArrowUp") {
            event.preventDefault();
            setPosition((current) => Math.min(100, current + SLIDER_STEP));
            return;
        }

        if (event.key === "Home") {
            event.preventDefault();
            setPosition(0);
            return;
        }

        if (event.key === "End") {
            event.preventDefault();
            setPosition(100);
        }
    };

    return (
        <article
            ref={containerRef}
            className="relative aspect-[352/520] select-none overflow-hidden rounded-2xl text-pureWhite lg:aspect-[3/4]"
        >
            <Image
                src={afterImageUrl}
                alt={alt}
                fill
                draggable={false}
                sizes="(min-width: 1024px) 360px, 100vw"
                loading="lazy"
                className="pointer-events-none object-cover object-center"
            />

            <div
                className="pointer-events-none absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
            >
                <Image
                    src={beforeImageUrl}
                    alt=""
                    fill
                    draggable={false}
                    sizes="(min-width: 1024px) 360px, 100vw"
                    loading="lazy"
                    className="object-cover object-center"
                />
            </div>

            <Overline size="md" className="pointer-events-none absolute left-5 top-5 z-10 text-pureWhite">
                Before
            </Overline>
            <Overline size="md" className="pointer-events-none absolute right-5 top-5 z-10 text-pureWhite">
                After
            </Overline>

            <div
                role="slider"
                tabIndex={0}
                aria-label={`Compare before and after for ${name}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(position)}
                aria-valuetext={`${Math.round(position)} percent after photo`}
                className="pointer-events-none absolute inset-y-0 z-[5] flex w-8 -translate-x-1/2 cursor-ew-resize touch-none flex-col items-center justify-center md:pointer-events-auto"
                style={{ left: `${position}%` }}
                onPointerDown={handlePointerDown}
                onKeyDown={handleKeyDown}
            >
                <div
                    className="pointer-events-none absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 bg-[linear-gradient(to_bottom,#fff_0%,#fff_55%,rgba(255,255,255,0.7)_70%,rgba(255,255,255,0.2)_88%,transparent_100%)]"
                    aria-hidden="true"
                />

                <span
                    aria-hidden="true"
                    className="pointer-events-auto relative flex size-16 touch-none items-center justify-center md:size-9"
                >
                    <span className="relative flex size-12 items-center justify-center rounded-full bg-pureWhite text-primary500 md:size-9">
                        <Image
                            src="https://res.cloudinary.com/dirscuuhp/image/upload/v1790607294/new_design/MouseScroll_zhapj2.svg"
                            alt=""
                            fill
                            draggable={false}
                            sizes="40px"
                            loading="lazy"
                            className="hidden object-contain object-center md:block"
                        />
                        <HandSwipeLeftIcon size={24} weight="light" className="md:hidden" />
                    </span>
                </span>

                <span
                    aria-hidden="true"
                    className="max-md:hidden relative mt-2 whitespace-nowrap rounded-full bg-pureWhite px-3 py-1.5 text-primary500"
                >
                    <Overline size="xs" className="pt-[2px]">Drag to compare</Overline>
                </span>
            </div>

            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-trueBlack/80 via-trueBlack/50 to-transparent px-5 pb-5 pt-16 lg:px-6 lg:pb-6">
                <Paragraph size="lg">
                    <span className="absolute -translate-x-full">“</span>
                    {quote}”
                </Paragraph>

                <Overline as="p" size="lg" className="mt-[10px]">
                    {name}, {age}
                </Overline>
                <Overline size="xs" className="mt-1 flex items-center gap-1">
                    <SealCheckIcon weight="fill" className="size-[18px] text-primary300" aria-hidden="true" />
                    <span className="inline-block pt-[2px]">Verified patient</span>
                </Overline>
            </div>
        </article>
    );
}
